import { AlertCircle, Check, Loader2, RefreshCw } from "lucide-react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";
import { cn } from "@/lib/utils";

import type { AvailableModel } from "./model-selection-api";
import { useModelSelection } from "./use-model-selection";

function ModelOption({
  model,
  isSelected,
  disabled,
  onSelect,
}: {
  model: AvailableModel;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (modelId: string) => void;
}): React.ReactElement {
  return (
    <div
      role="radio"
      aria-checked={isSelected}
      tabIndex={isSelected ? 0 : -1}
      data-testid={`model-option-${model.id}`}
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) onSelect(model.id);
      }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect(model.id);
        }
      }}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 rounded-lg border p-4 text-left transition-colors",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50 hover:bg-muted/50",
        disabled && "cursor-not-allowed opacity-60"
      )}
    >
      <div
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
          isSelected ? "border-primary bg-primary" : "border-muted-foreground"
        )}
      >
        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{model.name}</span>
          {isSelected && (
            <Badge variant="secondary" data-testid="active-badge">
              Active
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{model.provider}</span>
      </div>
    </div>
  );
}

export function ModelSelectionCard(): React.ReactElement {
  const {
    currentModelId,
    availableModels,
    isLoading,
    isSaving,
    error,
    saveSuccess,
    selectModel,
    refresh,
  } = useModelSelection();

  const handleSelect = (modelId: string): void => {
    if (modelId === currentModelId) return;
    void selectModel(modelId);
  };

  return (
    <Card data-testid="model-selection-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Model Selection</CardTitle>
            <CardDescription>
              Choose the Bedrock inference model for the assistant. Changes take effect on the next
              Lambda cold start.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isLoading || isSaving}
            data-testid="refresh-model-config"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div
            data-testid="model-selection-error"
            className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {saveSuccess && (
          <div
            data-testid="model-selection-success"
            className="mb-4 flex items-center gap-2 rounded-lg border border-green-500/50 bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400"
          >
            <Check className="h-4 w-4 shrink-0" />
            <span>{saveSuccess}</span>
          </div>
        )}

        {isLoading && (
          <div className="space-y-3" data-testid="model-selection-loading">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {!isLoading && (
          <div
            role="radiogroup"
            aria-label="Model selection"
            className="space-y-2"
            data-testid="model-options"
          >
            {availableModels.map((model) => (
              <ModelOption
                key={model.id}
                model={model}
                isSelected={model.id === currentModelId}
                disabled={isSaving}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}

        {isSaving && (
          <div
            className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="model-saving-indicator"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Saving model selection...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
